import { SeaSavedCardModel } from "entities/SeaSavedCard/model/model";
import { isSeaSearchShipmentModel, SeaSearchCardModel } from "entities/SeaSearchCard";
import { formatDate } from "shared/utils/dateTime.utils";
import { logger } from "shared/utils/debug.utils";
import { isAppComDomain } from "shared/utils/url.utils";

type SeaShipmentModel = SeaSavedCardModel | SeaSearchCardModel;

type DataLayer = {
  event?: string;
  eventCategory?: string;
  eventAction?: string;
  eventLabel?: string | number;
  funnel_entry?: string | number;
  from_location?: string;
  to_location?: string;
  value?: number;
  currency?: number;
};

const developDataLayersCache: DataLayer[] = [];
type DevelopDataLayersReferenceBinded = Record<
  string,
  {
    layer: DataLayer;
    date: string;
    model: SeaShipmentModel | undefined;
  }[]
>;
const developDataLayersReferenceBinded: DevelopDataLayersReferenceBinded = {};

export class SeaDataLayersManager {
  constructor(private model?: SeaShipmentModel) {}

  private pushToDataLayer({
    eventAction,
    eventLabel,
    fromLocation,
    toLocation,
    value,
    currency,
    dataOverride,
  }: {
    eventAction: string;
    eventLabel?: string | number;
    fromLocation?: string;
    toLocation?: string;
    value?: number;
    currency?: number;
    dataOverride?: Partial<DataLayer>;
  }) {
    const commonLayerOptions: DataLayer = {
      event: "dl_containertracking",
      eventCategory: "Container Tracking",
      funnel_entry: "Container Tracking",
      ...dataOverride,
    };

    const layer: DataLayer = {
      ...commonLayerOptions,
      eventAction,
      eventLabel,
    };

    if (fromLocation) {
      layer["from_location"] = fromLocation;
    }

    if (toLocation) {
      layer["to_location"] = toLocation;
    }

    if (value) {
      layer.value = value;
    }

    if (currency) {
      layer.currency = currency;
    }

    if (__PROD__ && isAppComDomain && window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(layer);
    }

    if (__DEV__) {
      const reference = this.model?.reference;

      developDataLayersCache.push(layer);
      logger("dataLayersCache", developDataLayersCache);

      if (!reference) return;

      if (!developDataLayersReferenceBinded[reference]) {
        developDataLayersReferenceBinded[reference] = [];
      }
      developDataLayersReferenceBinded[reference].push({
        layer,
        date: formatDate(new Date()),
        model: this.model,
      });

      logger<DevelopDataLayersReferenceBinded>("dataLayersDebug", null, (prev) => ({
        ...prev,
        [reference]: developDataLayersReferenceBinded[reference],
      }));
    }
  }

  public setModel(model: SeaShipmentModel): this {
    this.model = model;
    return this;
  }
  public handleSearch() {
    this.handleSearchResultSuccess();
    this.handleSearchResultError();
    this.handleSearchNoResult();
  }

  public searchInitiated(): this {
    this.pushToDataLayer({
      eventAction: "Search Initiated",
      eventLabel: "container number",
      dataOverride: { funnel_entry: "Freight Route" },
    });
    return this;
  }

  public onButtonClick(name: string): this {
    this.pushToDataLayer({
      eventAction: `${name} click`,
      dataOverride: { event: "dl_containertracking" },
    });
    return this;
  }

  public onClickSearchResult(): this {
    if (this.model?.reference) {
      this.pushToDataLayer({ eventAction: "Result Selected", eventLabel: this.model.reference });
    }
    return this;
  }

  public showRates({ reference, ...rest }: { reference: string; fromLocation: string; toLocation: string }): this {
    this.pushToDataLayer({ eventAction: "Show Rates", eventLabel: reference, ...rest });
    return this;
  }

  public searchLimitReached(): this {
    if (this.model?.reference) {
      this.pushToDataLayer({ eventAction: "Search Limit Reached", eventLabel: this.model?.reference });
    }
    return this;
  }

  public onClickPayButton({ shipmentCount, value }: { shipmentCount: number; value: number }): this {
    this.pushToDataLayer({
      eventAction: "Pay Click",
      eventLabel: shipmentCount,
      value,
      currency: value,
      dataOverride: { funnel_entry: value },
    });
    return this;
  }

  private handleSearchResultSuccess(): this {
    if (this.model?.reference) {
      this.pushToDataLayer({ eventAction: "Search Success", eventLabel: this.model.reference });
    }
    return this;
  }

  private handleSearchResultError(): this {
    if (isSeaSearchShipmentModel(this.model) && !this.model?.isSuccess && this.model?.reference) {
      this.pushToDataLayer({
        eventAction: `Error - ${this.model.message}`,
        eventLabel: this.model.reference,
      });
    }
    return this;
  }

  private handleSearchNoResult(): this {
    if (isSeaSearchShipmentModel(this.model) && !this.model?.hasData && this.model?.reference) {
      this.pushToDataLayer({ eventAction: "No Result", eventLabel: this.model.reference });
    }
    return this;
  }
}
